import Vue from 'vue'
import api from '../../api/operatorWorkspace'

export const chatKey = chat => `${chat.company_id}:${chat.id}`
export const mergeItems = (older, newer) => {
    const records = new Map(older.map(item => [`${item.kind}:${item.id}`, item]))
    newer.forEach(item => records.set(`${item.kind}:${item.id}`, item))
    return Array.from(records.values()).sort(
        (a, b) =>
            (a.date_created || '').localeCompare(b.date_created || '') || a.kind.localeCompare(b.kind) || a.id - b.id
    )
}
const initialState = () => ({
    companies: [],
    channels: [],
    filters: { company_ids: [], channel_ids: [], view: 'all', search: '' },
    items: [],
    counters: {},
    cursor: null,
    loading: false,
    error: '',
    enabled: false,
    tabs: {},
    order: [],
    activeKey: null,
    references: {},
    generation: 0,
    updatedAt: null,
})

// Запросы разных вкладок независимы. Повторные сигналы объединяются до завершения запроса.
const inFlight = new Map()
let tabInstance = 0

// crypto.randomUUID exists only in secure contexts; getRandomValues works on plain http as well.
function requestId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID()
    const bytes = window.crypto.getRandomValues(new Uint8Array(16))
    bytes[6] = (bytes[6] & 0x0f) | 0x40
    bytes[8] = (bytes[8] & 0x3f) | 0x80
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

// Only what defines access: the operator identity in the company and its capabilities.
// A changed name or an extra field in the payload must not close open tabs and drafts.
const companySignature = company =>
    JSON.stringify({
        id: company.company_id,
        user: company.user_company_id,
        capabilities: [...(company.capabilities || [])].sort(),
    })

// Fresh tab state for a chat that is opened for the first time.
function createTab(chat, key) {
    return {
        ...chat,
        waiting_for_reply: !!chat.waiting_for_reply,
        key,
        driverSummary: null,
        messages: [],
        previousMessages: [],
        previousCursor: null,
        previousLoaded: false,
        previousLoading: false,
        previousError: '',
        cursor: null,
        draft: '',
        file: null,
        pending: null,
        loading: false,
        sending: false,
        error: '',
        archived: false,
        scrollTop: null,
        historyMode: false,
        timelineNotice: '',
        instance: ++tabInstance,
        messageGeneration: 0,
    }
}

// Open tabs outside the current queue page still get a fresh status, without loading their messages.
// At most three detail requests run at a time; every result is applied only if nothing changed meanwhile.
async function syncUnlistedTabs({ state, commit }, { generation, page, companyIds }) {
    const listed = new Set(page.data.items.map(chatKey))
    const remaining = state.order
        .filter(
            key =>
                !listed.has(key) &&
                !state.tabs[key].archived &&
                (!companyIds || companyIds.includes(state.tabs[key].company_id))
        )
        .map(key => state.tabs[key])
    const current = tab => state.generation === generation && state.tabs[tab.key] === tab
    await Promise.all(
        Array.from({ length: Math.min(3, remaining.length) }, async () => {
            while (remaining.length && state.generation === generation) {
                const tab = remaining.shift()
                try {
                    const detail = await api.chat(tab)
                    if (current(tab) && !tab.archived) commit('tab', { key: tab.key, patch: detail.data })
                } catch (error) {
                    if (current(tab) && [403, 404].includes(error.status))
                        commit('tab', {
                            key: tab.key,
                            patch: { archived: true, is_mine: false, error: error.message },
                        })
                }
            }
        })
    )
}
function single(key, fn) {
    if (inFlight.has(key)) return inFlight.get(key)
    const work = Promise.resolve()
        .then(fn)
        .finally(() => inFlight.delete(key))
    inFlight.set(key, work)
    return work
}

export default {
    namespaced: true,
    state: initialState,
    getters: { active: state => state.tabs[state.activeKey] || null },
    mutations: {
        options(state, data) {
            state.channels = data.channels || []
        },
        context(state, companies) {
            const previous = new Map(state.companies.map(company => [company.company_id, companySignature(company)]))
            const changed = new Set(
                companies
                    .filter(
                        company =>
                            previous.has(company.company_id) &&
                            previous.get(company.company_id) !== companySignature(company)
                    )
                    .map(company => company.company_id)
            )
            if (
                JSON.stringify(state.companies.map(companySignature)) !== JSON.stringify(companies.map(companySignature))
            ) {
                state.generation++
                state.references = {}
            }
            state.companies = companies
            state.enabled = true
            const allowed = new Set(companies.map(company => company.company_id))
            state.items = state.items.filter(item => allowed.has(item.company_id))
            state.order.slice().forEach(key => {
                if (!allowed.has(state.tabs[key].company_id) || changed.has(state.tabs[key].company_id)) {
                    Vue.delete(state.tabs, key)
                    state.order.splice(state.order.indexOf(key), 1)
                }
            })
            if (!state.tabs[state.activeKey]) state.activeKey = state.order[0] || null
            state.filters.company_ids = state.filters.company_ids.filter(id => allowed.has(id))
            Object.keys(state.references).forEach(id => {
                if (!allowed.has(Number(id))) Vue.delete(state.references, id)
            })
        },
        filter(state, values) {
            state.filters = { ...state.filters, ...values }
            state.generation++
            state.cursor = null
            state.items = []
        },
        loading(state, value) {
            state.loading = value
        },
        error(state, value) {
            state.error = value
        },
        list(state, { page, counts, append }) {
            const records = new Map((append ? state.items : []).map(item => [chatKey(item), item]))
            page.data.items.forEach(item => {
                const key = chatKey(item)
                records.set(key, item)
                if (state.tabs[key] && !state.tabs[key].archived) Object.assign(state.tabs[key], item)
            })
            state.items = Array.from(records.values()).slice(0, 400)
            state.cursor = state.items.length < 400 ? page.meta.next_cursor : null
            if (counts) state.counters = counts.data
            state.updatedAt = Date.now()
            state.error = ''
        },
        open(state, chat) {
            const key = chatKey(chat)
            if (!state.tabs[key]) {
                Vue.set(state.tabs, key, createTab(chat, key))
                state.order.push(key)
            } else if (state.tabs[key].archived) {
                // Повторный выбор из серверной очереди открывает живой чат после возврата или восстановления доступа.
                Object.assign(state.tabs[key], chat, {
                    archived: false,
                    error: '',
                    historyMode: false,
                    timelineNotice: '',
                    previousMessages: [],
                    previousCursor: null,
                    previousLoaded: false,
                    previousLoading: false,
                    previousError: '',
                })
            }
            if (chat.preferred_channel_id) Vue.set(state.tabs[key], 'preferred_channel_id', chat.preferred_channel_id)
            state.activeKey = key
        },
        select(state, key) {
            if (state.tabs[key]) state.activeKey = key
        },
        close(state, key) {
            Vue.delete(state.tabs, key)
            state.order = state.order.filter(item => item !== key)
            if (state.activeKey === key) state.activeKey = state.order[state.order.length - 1] || null
        },
        tab(state, { key, patch }) {
            const tab = state.tabs[key]
            if (!tab) return
            Object.assign(tab, patch)
            if (patch.pending_send && !tab.pending)
                tab.pending = { id: patch.pending_send.client_request_id, recovered: true }
        },
        invalidateReads(state, key) {
            // Ответы, начатые до назначения или возврата, не должны восстанавливать старого ответственного.
            state.generation++
            state.loading = false
            const tab = state.tabs[key]
            if (tab) {
                tab.messageGeneration++
                tab.loading = false
                tab.previousLoading = false
            }
        },
        reference(state, { companyId, data }) {
            Vue.set(state.references, companyId, data)
        },
        reset(state) {
            const generation = state.generation + 1
            Object.assign(state, initialState(), { generation })
        },
    },
    actions: {
        async context({ state, commit }) {
            const generation = state.generation
            try {
                const result = await api.context()
                if (state.generation !== generation) return null
                commit('context', result.data.companies)
                commit('options', result.data)
                return result.data
            } catch (error) {
                if (state.generation !== generation) return null
                if ([401, 403, 404].includes(error.status)) commit('reset')
                commit('error', error.message)
                throw error
            }
        },
        async refresh({ state, commit }, { append = false, companyIds = null } = {}) {
            const generation = state.generation
            const filters = {
                ...state.filters,
                company_ids: [...state.filters.company_ids],
                limit: append ? 50 : Math.min(100, Math.max(50, state.items.length)),
            }
            if (append) filters.cursor = state.cursor
            if (append && !filters.cursor) return
            return single(`queue:${generation}`, async () => {
                commit('loading', true)
                try {
                    const page = await api.inbox({ ...filters, include_counters: append ? 0 : 1 })
                    // Совместимость с сервером до обновления: отдельный endpoint остаётся доступен.
                    const counts = append
                        ? null
                        : page.data.counters
                        ? { data: page.data.counters }
                        : await api.counters(filters)
                    const target = Math.min(400, state.items.length)
                    while (
                        !append &&
                        state.generation === generation &&
                        page.meta.next_cursor &&
                        page.data.items.length < target
                    ) {
                        const next = await api.inbox({ ...filters, cursor: page.meta.next_cursor })
                        page.data.items.push(...next.data.items)
                        page.meta = next.meta
                    }
                    if (state.generation === generation) {
                        commit('list', { page, counts, append })
                        await syncUnlistedTabs({ state, commit }, { generation, page, companyIds })
                    }
                } catch (error) {
                    if (state.generation === generation) commit('error', error.message)
                    throw error
                } finally {
                    if (state.generation === generation) commit('loading', false)
                }
            })
        },
        async open({ commit, dispatch }, chat) {
            commit('open', chat)
            await dispatch('messages', { key: chatKey(chat) })
        },
        async messages({ state, commit }, { key, older = false } = {}) {
            const tab = state.tabs[key]
            if (!tab || tab.archived) return
            const cursor = older ? tab.cursor : null
            if (older && !cursor) return
            const generation = tab.messageGeneration
            const current = () => state.tabs[key] === tab && tab.messageGeneration === generation
            return single(`messages:${key}:${tab.instance}:${generation}:${cursor || ''}`, async () => {
                commit('tab', { key, patch: { loading: true, error: '' } })
                try {
                    const [detail, page] = await Promise.all([api.chat(tab), api.messages(tab, { limit: 20, cursor })])
                    if (!current()) return
                    if (!older && tab.historyMode) {
                        commit('tab', { key, patch: detail.data })
                        return
                    }
                    const merged = mergeItems(tab.messages, page.data.items)
                    const oldKeys = new Set(tab.messages.map(item => `${item.kind}:${item.id}`))
                    const gap =
                        !older &&
                        tab.messages.length &&
                        page.meta.has_more &&
                        !page.data.items.some(item => oldKeys.has(`${item.kind}:${item.id}`))
                    // При большом потоке не соединяем разорванные участки и ограничиваем DOM каждой вкладки.
                    const restart = !older && (merged.length > 400 || gap)
                    commit('tab', {
                        key,
                        patch: {
                            ...detail.data,
                            messages: restart ? mergeItems([], page.data.items) : merged,
                            historyMode: older,
                            timelineNotice: restart
                                ? 'Показан следующий участок переписки. Вся история доступна постранично.'
                                : tab.timelineNotice,
                            cursor: older || restart || !tab.messages.length ? page.meta.next_cursor : tab.cursor,
                        },
                    })
                } catch (error) {
                    if (!current()) return
                    if (current()) {
                        if ([401, 403, 404].includes(error.status))
                            commit('tab', {
                                key,
                                patch: { messages: [], is_mine: false, archived: true, error: error.message },
                            })
                        else commit('tab', { key, patch: { error: error.message } })
                    }
                    throw error
                } finally {
                    if (current()) commit('tab', { key, patch: { loading: false } })
                }
            })
        },
        async earlier({ state, commit, dispatch }, key) {
            const tab = state.tabs[key]
            if (!tab || tab.loading || tab.previousLoading) return
            if (tab.cursor && !tab.archived) return dispatch('messages', { key, older: true })
            if (!tab.customer_id || (tab.previousLoaded && !tab.previousCursor)) return
            const generation = tab.messageGeneration
            const current = () => state.tabs[key] === tab && tab.messageGeneration === generation
            commit('tab', { key, patch: { previousLoading: true, previousError: '' } })
            try {
                const page = await api.history(tab, { limit: 30, cursor: tab.previousCursor })
                if (!current()) return
                commit('tab', {
                    key,
                    patch: {
                        previousMessages: mergeItems(
                            tab.previousMessages,
                            page.data.items.map(row => ({ ...row, archived: true }))
                        ),
                        previousCursor: page.meta.next_cursor,
                        previousLoaded: true,
                    },
                })
            } catch (error) {
                if (!current()) return
                commit('tab', {
                    key,
                    patch: {
                        previousError: error.message,
                        ...([401, 403, 404].includes(error.status)
                            ? { previousMessages: [], previousCursor: null, previousLoaded: false }
                            : {}),
                    },
                })
            } finally {
                if (current()) commit('tab', { key, patch: { previousLoading: false } })
            }
        },
        async reference({ state, commit }, companyId) {
            if (!state.references[companyId]) {
                const generation = state.generation
                const response = await api.reference(companyId)
                if (
                    state.generation === generation &&
                    state.companies.some(company => company.company_id === companyId)
                )
                    commit('reference', { companyId, data: response.data })
            }
        },
        async action({ state, commit, dispatch }, { key, action, payload = {} }) {
            const tab = state.tabs[key]
            if (!tab) return
            const response = await api.action(tab, action, payload)
            if (state.tabs[key] !== tab) return response
            commit('invalidateReads', key)
            if (action === 'finish' || action === 'transfer') {
                commit('tab', { key, patch: { archived: true, is_mine: false } })
            } else if (action === 'return-to-queue') {
                commit('tab', { key, patch: { archived: false, is_mine: false, responsible_user_id: null } })
            } else if (action === 'assign' && response.data?.is_mine === true) {
                commit('tab', { key, patch: { archived: false, is_mine: true, error: '' } })
            }
            // Подтверждённое действие не повторяется из-за ошибки последующего чтения очереди.
            try {
                if (!['finish', 'return-to-queue', 'transfer'].includes(action)) await dispatch('messages', { key })
                await dispatch('refresh')
            } catch (error) {
                commit('error', error.message)
            }
            return response
        },
        async read({ state, commit }, key) {
            const tab = state.tabs[key]
            if (!tab || !tab.is_mine || tab.archived || document.hidden || key !== state.activeKey) return
            const unread = tab.messages.filter(
                item => item.kind === 'message' && item.sender_id === 'customer' && !item.is_read
            )
            if (!unread.length) return
            const through = Math.max(...tab.messages.filter(item => item.kind === 'message').map(item => item.id))
            await single(`read:${key}:${through}`, () => api.action(tab, 'read', { through_message_id: through }))
            if (state.tabs[key] === tab)
                commit('tab', {
                    key,
                    patch: {
                        messages: tab.messages.map(item =>
                            item.kind === 'message' && item.id <= through ? { ...item, is_read: true } : item
                        ),
                    },
                })
        },
        async send({ state, commit, dispatch }, key) {
            const tab = state.tabs[key]
            if (!tab || tab.sending || !tab.is_mine || tab.archived || (!tab.draft.trim() && !tab.file)) return
            if (tab.pending) return dispatch('checkSend', key)
            // Повтор после таймаута использует тот же UUID и неизменное содержимое.
            const pending = tab.pending || {
                id: requestId(),
                text: tab.draft,
                file: tab.file,
                channelId: tab.preferred_channel_id || tab.company_platform_id,
            }
            commit('tab', { key, patch: { sending: true, pending, error: '' } })
            const payload = new FormData()
            payload.append('client_request_id', pending.id)
            if (pending.channelId) payload.append('company_platform_id', pending.channelId)
            payload.append('type', pending.file ? 'media' : 'text')
            if (pending.file) payload.append('file', pending.file)
            else payload.append('message', pending.text)
            let confirmed = false
            try {
                await api.action(tab, 'messages', payload)
                confirmed = true
                if (state.tabs[key] === tab)
                    commit('tab', {
                        key,
                        patch: {
                            pending: null,
                            draft: !pending.file && tab.draft === pending.text ? '' : tab.draft,
                            file: tab.file === pending.file ? null : tab.file,
                            ...(tab.historyMode
                                ? { historyMode: false, messages: [], cursor: null, timelineNotice: '' }
                                : {}),
                        },
                    })
                await dispatch('messages', { key })
                await dispatch('refresh')
            } catch (error) {
                if (state.tabs[key] === tab)
                    commit('tab', {
                        key,
                        patch: {
                            error: error.message,
                            pending: confirmed || [400, 403, 404, 422].includes(error.status) ? null : pending,
                        },
                    })
                throw error
            } finally {
                if (state.tabs[key] === tab) commit('tab', { key, patch: { sending: false } })
            }
        },
        async checkSend({ state, commit, dispatch }, key) {
            const tab = state.tabs[key]
            if (!tab?.pending || tab.sending) return
            const pending = tab.pending
            commit('tab', { key, patch: { sending: true, error: '' } })
            try {
                const response = await api.sendStatus(tab, pending.id)
                if (state.tabs[key] !== tab) return
                const done = response.data.state === 'done'
                const failed = response.data.state === 'failed'
                commit('tab', {
                    key,
                    patch: {
                        pending: done || failed ? null : pending,
                        draft: done && !pending.file && tab.draft === pending.text ? '' : tab.draft,
                        file: done && tab.file === pending.file ? null : tab.file,
                        error: done
                            ? ''
                            : failed
                            ? 'Отправка не состоялась. Можно отправить сообщение снова.'
                            : 'Результат пока не подтверждён. Требуется сверка с каналом доставки.',
                    },
                })
                await dispatch('messages', { key })
            } catch (error) {
                if (state.tabs[key] === tab) commit('tab', { key, patch: { error: error.message } })
                throw error
            } finally {
                if (state.tabs[key] === tab) commit('tab', { key, patch: { sending: false } })
            }
        },
    },
}
